// Projects — every project you can open, as a list or as cards.
//
// The Claude Design, pixel for pixel (screens_projects.jsx; owner 2026-10-15:
// "these exact things exactly as it, without removing existing things"): the
// snapshot strip, the status tabs with ★, search, sort, the list/cards toggle,
// New project, the list rows and the cards with their coloured stripe. Every
// figure is real or "—":
// - Budget is the deal's value, from crm_deals (or, with no deal linked, the
//   amount typed in: project_deal_values), which RLS hands back only to a
//   project's manager and pipeline:full (owner, admin); a deal in another
//   currency is never converted, so "—". What has been spent is the project's
//   cost from money_overview, which answers owner and admin only; anyone else
//   reads "—". People who can't see deals get the layout without budget.
// - Hours logged come from project_metrics, which reads for the same people.
// - Last activity is when the project's newest-changed work item changed.
// - Stars are yours alone (project_stars). Where that table isn't there yet,
//   the star and ★ stay away rather than fail.
// "New project" opens Create project (spec §6.1); a won Zoho deal still sets
// one up by itself. Rows and cards are links. RLS decides what comes back.
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { can } from '../lib/roles';
import { useOrgDates } from '../lib/orgSettings';
import { addDaysToKey, fmtDate, fmtRelative, orgDateKey, todayIso } from '../lib/dates';
import { fmtINR } from '../lib/money';
import { loadMoneyOverview, type MoneyProject } from '../lib/finance';
import { useBelow } from '../lib/useMediaQuery';
import {
  daysUntil,
  dealSetupError,
  dueWithin,
  projectState,
  sortProjects,
  stageIndex,
  stageName,
  type ProjectState,
  type SortKey,
} from '../lib/projects';
import { AvatarGroup, Button, Icon, Mono, ProjectID, Select } from '../components/primitives';
import { SegmentedControl } from '../components/card';
import { Dialog } from '../components/dialog';
import { Field, TextInput } from '../components/field';
import { LoadError, useToast } from '../components/feedback';
import { Menu, type MenuItem } from '../components/menu';
import {
  BudgetBar,
  MiniStages,
  SnapshotTile,
  STAGE_COLOR,
  STATE_COLOR,
  StarButton,
  StatusDot,
  costCaveat,
  projectColor,
} from '../components/projects';
import { CreateProjectDialog } from './project/CreateProjectDialog';

type Row = {
  id: string;
  name: string;
  /** The project's key (MC); the legacy code only before a key existed. */
  key: string | null;
  code: string | null;
  client: string | null;
  status: string;
  health: string | null;
  progress: number;
  deadline: string | null;
  stage: string | null;
  stage_idx: number;
  lead: string | null;
  zoho_deal_id: string | null;
  project_members: { user_id: string; profiles: { full_name: string | null } | null }[];
  project_skill_reqs?: { skills: { name: string } | null }[] | null;
  /** The project's most recently changed work item (one row). */
  tasks?: { updated_at: string }[] | null;
};
type Project = Row & {
  state: ProjectState;
  team: string[];
  leadName: string | null;
  skills: string[];
  step: number;
  /** The deal's value in ₹: only when crm_deals answered, and the deal is in INR. */
  budget: number | null;
  /** Its life-to-date cost: only when money_overview answered (owner, admin). */
  fin: MoneyProject | null;
  /** Logged hours: only when project_metrics answered. */
  hours: number | null;
  /** When a work item last changed: the card's "Last activity". */
  activity: string | null;
};

type Filter = 'all' | ProjectState | 'starred';
type View = 'table' | 'cards';

// The viewer's own choice, so two people sharing a browser keep theirs.
const viewKey = (uid: string | undefined) => `hf.projects.view.${uid ?? 'anon'}`;
const readView = (uid: string | undefined): View => {
  try {
    return localStorage.getItem(viewKey(uid)) === 'cards' ? 'cards' : 'table';
  } catch {
    return 'table';
  }
};

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'delivery', label: 'Delivery date' },
  { value: 'progress', label: 'Progress' },
  { value: 'client', label: 'Client A–Z' },
];

export function ProjectsScreen() {
  const { session, perms } = useAuth();
  const uid = session?.user?.id;
  const { push } = useToast();
  const { tz, dateFormat, weekStartsOn } = useOrgDates();
  const today = orgDateKey(Date.now(), tz);
  const phone = useBelow('md');
  const [rows, setRows] = useState<Project[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [stars, setStars] = useState<Set<string> | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<SortKey>('delivery');
  const [view, setViewState] = useState<View>(() => readView(uid));
  const [dialog, setDialog] = useState<'create' | 'deal' | null>(null);
  const canCreate = can(perms, 'projects', 'full');
  // Cost answers only both; the server decides, this only skips a refused call.
  const costs = can(perms, 'people', 'full') && can(perms, 'pipeline', 'full');

  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(viewKey(uid), v);
    } catch {
      // Private window or blocked storage: the choice lasts this visit.
    }
  };

  const load = useCallback(async () => {
    const sb = supabase;
    if (!sb) return;
    setFailed(false);
    const [pr, deals, typed, metrics, starred, overview] = await Promise.all([
      sb
        .from('projects')
        .select(
          'id, name, key, code, client, status, health, progress, deadline, stage, stage_idx, lead, zoho_deal_id, project_members(user_id, profiles(full_name)), project_skill_reqs(skills(name)), tasks(updated_at)',
        )
        .order('updated_at', { referencedTable: 'tasks', ascending: false })
        .limit(1, { referencedTable: 'tasks' }),
      // Both answer only for the rows this person may see the money of.
      sb.from('crm_deals').select('zoho_deal_id, amount, currency'),
      // A deal value typed in, for a project with no Zoho deal (same people).
      sb.from('project_deal_values').select('project_id, amount'),
      sb.from('project_metrics').select('project_id, hours_logged'),
      sb.from('project_stars').select('project_id'),
      // Cost is whole-life whatever the range; one day keeps the call light.
      // Its projects are the ones not done (plus any timed that day).
      costs ? loadMoneyOverview(todayIso(), todayIso()) : null,
    ]);
    if (pr.error) {
      setFailed(true);
      return;
    }
    const amount = new Map(
      (
        (deals.data as
          { zoho_deal_id: string; amount: number | null; currency?: string | null }[] | null) ?? []
      ).map((d) => [d.zoho_deal_id, !d.currency || d.currency === 'INR' ? d.amount : null]),
    );
    const typedAmount = new Map(
      ((typed.data as { project_id: string; amount: number | string }[] | null) ?? []).map((d) => [
        d.project_id,
        Number(d.amount),
      ]),
    );
    const fin = new Map(
      ((overview?.ok && overview.data?.projects) || []).map((m) => [m.project_id, m]),
    );
    const hours = new Map(
      ((metrics.data as { project_id: string; hours_logged: number }[] | null) ?? []).map((m) => [
        m.project_id,
        Number(m.hours_logged),
      ]),
    );
    setStars(
      starred.error
        ? null
        : new Set(
            ((starred.data as { project_id: string }[] | null) ?? []).map((s) => s.project_id),
          ),
    );
    setRows(
      ((pr.data as unknown as Row[]) ?? []).map((r) => {
        const team = (r.project_members ?? []).map((m) => ({
          id: m.user_id,
          name: m.profiles?.full_name || 'Teammate',
        }));
        // A linked deal's amount wins; with none, the amount typed in.
        const deal = r.zoho_deal_id ? amount.get(r.zoho_deal_id) : typedAmount.get(r.id);
        return {
          ...r,
          code: r.key ?? r.code,
          state: projectState(r),
          step: stageIndex(r),
          team: team.map((t) => t.name),
          leadName: team.find((t) => t.id === r.lead)?.name ?? null,
          skills: (r.project_skill_reqs ?? []).flatMap((s) => (s.skills ? [s.skills.name] : [])),
          budget: deal != null ? Number(deal) : null,
          fin: fin.get(r.id) ?? null,
          hours: hours.get(r.id) ?? null,
          activity: r.tasks?.[0]?.updated_at ?? null,
        };
      }),
    );
  }, [costs]);
  useEffect(() => {
    void load();
  }, [load]);

  const toggleStar = async (p: Project) => {
    if (!supabase || !stars) return;
    const on = !stars.has(p.id);
    const next = new Set(stars);
    if (on) next.add(p.id);
    else next.delete(p.id);
    setStars(next);
    const { error } = on
      ? await supabase.from('project_stars').insert({ project_id: p.id })
      : await supabase.from('project_stars').delete().eq('project_id', p.id);
    if (error) {
      setStars(stars);
      push({
        tone: 'error',
        message: `Couldn’t ${on ? 'star' : 'unstar'} ${p.name}: ${error.message}`,
      });
    }
  };

  const all = useMemo(() => rows ?? [], [rows]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: all.length,
      active: 0,
      risk: 0,
      blocked: 0,
      shipped: 0,
      starred: all.filter((p) => stars?.has(p.id)).length,
    };
    all.forEach((p) => c[p.state]++);
    return c;
  }, [all, stars]);
  const shown = useMemo(() => {
    const s = query.trim().toLowerCase();
    return sortProjects(
      all.filter(
        (p) =>
          (filter === 'all' || (filter === 'starred' ? stars?.has(p.id) : p.state === filter)) &&
          (!s ||
            [p.name, p.code, p.client, ...p.skills].some((v) =>
              (v ?? '').toLowerCase().includes(s),
            )),
      ),
      sort,
    );
  }, [all, filter, query, sort, stars]);

  if (!supabase) {
    return (
      <div style={{ padding: 40, color: 'var(--hf-ink-3)' }}>
        Supabase isn’t configured — set <Mono>VITE_SUPABASE_URL</Mono> /{' '}
        <Mono>VITE_SUPABASE_ANON_KEY</Mono>.
      </div>
    );
  }

  const loaded = rows != null;
  const isStarred = stars ? (p: Project) => stars.has(p.id) : null;
  const inFlight = all.filter((p) => p.state !== 'shipped');
  const attention = counts.risk + counts.blocked;
  const overdue = inFlight.filter((p) => p.deadline && p.deadline < today).length;
  const dueSoon = all.filter((p) => dueWithin(p, today)).length;
  const budgets = all.filter((p) => p.budget != null);
  const budgetTotal = budgets.reduce((s, p) => s + (p.budget ?? 0), 0);
  // Owner and admin: cost of deal value, over the projects that have both.
  const costed = budgets.filter((p) => p.fin);
  const sum = (f: (m: MoneyProject) => number) =>
    costed.reduce((s, p) => s + f(p.fin as MoneyProject), 0);
  const spent = sum((m) => m.cost);
  const spentOf = costed.reduce((s, p) => s + (p.budget ?? 0), 0);
  const caveat = costCaveat({
    freelancer_hours: sum((m) => m.freelancer_hours),
    uncosted_hours: sum((m) => m.uncosted_hours),
  });
  const dated = inFlight.filter((p) => p.deadline && p.deadline >= today);
  const avgDays = dated.length
    ? Math.round(dated.reduce((s, p) => s + daysUntil(p.deadline!, today), 0) / dated.length)
    : null;
  // The rest of the org's week, for "shipping this week".
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay();
  const weekEnd = addDaysToKey(today, 6 - ((dow - weekStartsOn + 7) % 7));
  const thisWeek = dated.filter((p) => p.deadline! <= weekEnd).length;
  const money = budgets.length > 0;
  const cards = view === 'cards' || phone;
  const statusTabs: { value: Filter; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'active', label: 'Active', count: counts.active },
    { value: 'risk', label: 'At-risk', count: counts.risk },
    { value: 'blocked', label: 'Blocked', count: counts.blocked },
    { value: 'shipped', label: 'Shipped', count: counts.shipped },
    ...(stars ? [{ value: 'starred' as const, label: '★', count: counts.starred }] : []),
  ];
  const filterWord =
    filter === 'all'
      ? 'all statuses'
      : filter === 'starred'
        ? 'starred'
        : statusTabs.find((t) => t.value === filter)!.label.toLowerCase();

  return (
    <div
      style={{
        padding: phone ? 'var(--hf-space-4)' : '24px 28px 60px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        maxWidth: 1440,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Snapshot strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${phone ? 150 : 200}px), 1fr))`,
          gap: 10,
        }}
      >
        <SnapshotTile
          kicker="Active"
          value={loaded ? inFlight.length : null}
          unit={`/ ${all.length}`}
          sub={
            stars
              ? `${counts.starred} starred · ${dueSoon} due in 14 days`
              : `${dueSoon} due in 14 days`
          }
          color="var(--hf-accent-text)"
        />
        <SnapshotTile
          kicker="Needs attention"
          value={loaded ? attention : null}
          unit="projects"
          sub={`${counts.risk} at-risk · ${counts.blocked} blocked · ${overdue} overdue`}
          color={attention ? 'var(--hf-amber-text)' : 'var(--hf-ink-3)'}
        />
        {money &&
          (costed.length ? (
            <SnapshotTile
              kicker="Budget used"
              value={fmtINR(spent)}
              sub={`of ${fmtINR(spentOf)} across ${costed.length} deal${costed.length === 1 ? '' : 's'}${caveat && ` · ${caveat}`}`}
              color="var(--hf-sky-text)"
              progress={spentOf > 0 ? Math.min(1, spent / spentOf) : 0}
            />
          ) : (
            <SnapshotTile
              kicker="Budget used"
              value={null}
              sub={`of ${fmtINR(budgetTotal)} across ${budgets.length} deal${budgets.length === 1 ? '' : 's'}`}
              color="var(--hf-sky-text)"
              progress={0}
            />
          ))}
        <SnapshotTile
          kicker="Avg. delivery"
          value={loaded ? avgDays : null}
          unit="days"
          sub={`${thisWeek} shipping this week`}
          color="var(--hf-lilac-text)"
        />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <SegmentedControl
          label="Project status"
          options={statusTabs}
          value={filter}
          onChange={setFilter}
          full={phone}
        />
        <TextInput
          dense
          type="search"
          aria-label="Search projects"
          placeholder="Search projects, clients, skills…"
          value={query}
          onChange={setQuery}
          style={{ flex: phone ? '1 1 100%' : '0 1 260px' }}
          leading={
            <Icon name="search" size={13} style={{ color: 'var(--hf-ink-3)', flexShrink: 0 }} />
          }
          trailing={
            query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery('')}
                className="hf-hit"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 2,
                  color: 'var(--hf-ink-3)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                }}
              >
                <Icon name="x" size={11} />
              </button>
            )
          }
        />
        <div style={{ flex: 1 }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--hf-ink-3)' }}>sort</span>
          <Select
            ariaLabel="Sort projects by"
            value={sort}
            options={SORTS}
            onChange={(v) => setSort(v as SortKey)}
          />
        </span>
        {!phone && (
          <div role="group" aria-label="Show projects as" className="hf-screen-bar">
            {(
              [
                { k: 'table', icon: 'list', label: 'List' },
                { k: 'cards', icon: 'grid', label: 'Cards' },
              ] as const
            ).map((m) => (
              <button
                key={m.k}
                type="button"
                aria-pressed={view === m.k}
                aria-label={m.label}
                title={m.label}
                onClick={() => setView(m.k)}
                className={view === m.k ? 'hf-screen-chip hf-hit active' : 'hf-screen-chip hf-hit'}
                style={{ padding: '5px 8px', display: 'inline-flex', alignItems: 'center' }}
              >
                <Icon name={m.icon} size={13} />
              </button>
            ))}
          </div>
        )}
        {canCreate && (
          <Button variant="primary" icon="plus" onClick={() => setDialog('create')}>
            New project
          </Button>
        )}
      </div>

      {failed ? (
        <LoadError what="projects" onRetry={() => void load()} />
      ) : !loaded ? (
        <div role="status" style={{ padding: 24, fontSize: 12.5, color: 'var(--hf-ink-3)' }}>
          Loading…
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '0 2px' }}>
            <span
              role="status"
              style={{ fontFamily: 'var(--hf-font-display)', fontSize: 15, color: 'var(--hf-ink)' }}
            >
              {shown.length} project{shown.length === 1 ? '' : 's'}
            </span>
            <Mono dim>{filterWord}</Mono>
            {query.trim() && <Mono dim>· filter: “{query.trim()}”</Mono>}
          </div>
          {shown.length > 0 &&
            (cards ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
                  gap: 12,
                }}
              >
                {shown.map((p) => (
                  <ProjectCard
                    key={p.id}
                    p={p}
                    today={today}
                    dateFormat={dateFormat}
                    star={isStarred ? { on: isStarred(p), toggle: () => void toggleStar(p) } : null}
                  />
                ))}
              </div>
            ) : (
              <ProjectsTable
                rows={shown}
                today={today}
                dateFormat={dateFormat}
                money={money}
                isStarred={isStarred}
                toggleStar={(p) => void toggleStar(p)}
              />
            ))}
          {all.length === 0 ? (
            <Empty title="No projects you can open">
              {canCreate
                ? 'Create one with New project, or a won Zoho deal sets one up by itself.'
                : 'A project appears here once it is created, or a won Zoho deal sets it up, and you’re on its team.'}
            </Empty>
          ) : (
            shown.length === 0 && (
              <Empty title="No projects match">
                Try clearing filters, or{' '}
                <button
                  type="button"
                  onClick={() => {
                    setFilter('all');
                    setQuery('');
                  }}
                  style={linkButton}
                >
                  reset
                </button>
                .
              </Empty>
            )
          )}
        </>
      )}

      {dialog === 'create' && (
        <CreateProjectDialog onClose={() => setDialog(null)} onFromDeal={() => setDialog('deal')} />
      )}
      {dialog === 'deal' && <SetUpFromDeal onClose={() => setDialog(null)} />}
    </div>
  );
}

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'var(--hf-accent-text)',
  textDecoration: 'underline',
  cursor: 'pointer',
};

const Empty = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div
    className="hf-glass"
    style={{ padding: 40, borderRadius: 14, textAlign: 'center', color: 'var(--hf-ink-3)' }}
  >
    <div style={{ fontSize: 14, marginBottom: 4, color: 'var(--hf-ink-2)' }}>{title}</div>
    <div style={{ fontSize: 11.5 }}>{children}</div>
  </div>
);

/** The delivery date and its countdown, in the design's colours. */
const Delivery = ({ p, today, dateFormat }: { p: Project; today: string; dateFormat: string }) => {
  const shipped = p.state === 'shipped';
  const d = p.deadline ? daysUntil(p.deadline, today) : null;
  const color = shipped
    ? 'var(--hf-sage-text)'
    : d == null
      ? 'var(--hf-ink-3)'
      : d < 0
        ? 'var(--hf-rose-text)'
        : d <= 14
          ? 'var(--hf-amber-text)'
          : 'var(--hf-ink)';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Mono style={{ fontSize: 11.5, color }}>
        {p.deadline ? fmtDate(p.deadline, dateFormat) : 'No date'}
      </Mono>
      <span
        style={{
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          fontFamily: 'var(--hf-font-mono)',
        }}
      >
        {shipped
          ? 'shipped'
          : d == null
            ? '—'
            : d < 0
              ? `${-d}d overdue`
              : d === 0
                ? 'today'
                : `in ${d}d`}
      </span>
    </span>
  );
};

const progressColor = (s: ProjectState) =>
  s === 'risk'
    ? 'var(--hf-amber)'
    : s === 'blocked'
      ? 'var(--hf-rose)'
      : s === 'shipped'
        ? 'var(--hf-sage)'
        : 'var(--hf-accent)';

const Bar = ({ p, row }: { p: Project; row?: boolean }) => (
  <span
    role="progressbar"
    aria-label={`${p.name} progress`}
    aria-valuenow={p.progress}
    aria-valuemin={0}
    aria-valuemax={100}
    style={{
      // In a row it takes the space left; stacked, the full width.
      flex: row ? 1 : 'none',
      display: 'block',
      height: 3,
      background: 'var(--hf-rule-2)',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <span
      style={{
        display: 'block',
        height: '100%',
        width: `${p.progress}%`,
        background: progressColor(p.state),
        boxShadow: p.state === 'active' ? `0 0 6px ${STATE_COLOR.active}` : undefined,
      }}
    />
  </span>
);

const StageDot = ({ p }: { p: Project }) => (
  <span
    aria-hidden="true"
    style={{
      width: 6,
      height: 6,
      borderRadius: 999,
      background: STAGE_COLOR[stageName(p.step)] ?? 'var(--hf-accent)',
    }}
  />
);

const rowMenu = (
  p: Project,
  navigate: (to: string) => void,
  star: (() => void) | null,
  starred: boolean,
): MenuItem[] => [
  {
    label: 'Open project',
    icon: 'arrowRight',
    onSelect: () => navigate(`/project?id=${p.id}`),
  },
  {
    label: 'Open board',
    icon: 'grid',
    onSelect: () => navigate(`/project?id=${p.id}&tab=board`),
  },
  ...(star ? [{ label: starred ? 'Unstar' : 'Star', icon: 'award' as const, onSelect: star }] : []),
];

const ProjectsTable = ({
  rows,
  today,
  dateFormat,
  money,
  isStarred,
  toggleStar,
}: {
  rows: Project[];
  today: string;
  dateFormat: string;
  money: boolean;
  isStarred: ((p: Project) => boolean) | null;
  toggleStar: (p: Project) => void;
}) => {
  const navigate = useNavigate();
  // The design's columns, less Budget for those who can't see deals.
  const inner = `90px minmax(0, 1.8fr) minmax(0, 1fr) 110px 90px ${money ? '110px ' : ''}130px`;
  return (
    <div className="hf-glass" style={{ borderRadius: 12, overflow: 'hidden' }}>
      <div
        aria-hidden="true"
        style={{
          display: 'grid',
          gridTemplateColumns: '28px 1fr 36px',
          gap: 10,
          padding: '10px 16px',
          borderBottom: '1px solid var(--hf-rule)',
          background: 'var(--hf-surface)',
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        <span />
        <span style={{ display: 'grid', gridTemplateColumns: inner, gap: 10 }}>
          <span>ID</span>
          <span>Project</span>
          <span>Team</span>
          <span>Stage</span>
          <span>Progress</span>
          {money && <span>Budget</span>}
          <span>Delivery</span>
        </span>
        <span />
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((p, i) => (
          <li
            key={p.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr 36px',
              alignItems: 'center',
              gap: 10,
              padding: '0 16px',
              borderTop: i ? '1px solid var(--hf-rule)' : 0,
            }}
          >
            {isStarred ? (
              <StarButton on={isStarred(p)} name={p.name} onToggle={() => toggleStar(p)} />
            ) : (
              <span />
            )}
            <Link
              to={`/project?id=${p.id}`}
              className="hf-row-link"
              style={{
                display: 'grid',
                gridTemplateColumns: inner,
                gap: 10,
                alignItems: 'center',
                padding: '12px 0',
                color: 'inherit',
                textDecoration: 'none',
                fontSize: 13,
                minWidth: 0,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  aria-hidden="true"
                  style={{ width: 3, height: 16, borderRadius: 2, background: projectColor(p.id) }}
                />
                <ProjectID id={p.code ?? '—'} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={ellipsis}>{p.name}</span>
                  <StatusDot state={p.state} />
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 11,
                    color: 'var(--hf-ink-3)',
                    minWidth: 0,
                  }}
                >
                  <span style={{ color: 'var(--hf-ink-2)', whiteSpace: 'nowrap' }}>
                    {p.client ?? 'Internal'}
                  </span>
                  {p.skills.length > 0 && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span
                        style={{
                          ...ellipsis,
                          color: 'var(--hf-ink-3)',
                          fontFamily: 'var(--hf-font-mono)',
                        }}
                      >
                        {p.skills.slice(0, 3).join(' · ').toLowerCase()}
                      </span>
                    </>
                  )}
                </span>
              </span>
              <Team p={p} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StageDot p={p} />
                  <span style={{ fontSize: 12, color: 'var(--hf-ink-2)' }}>
                    {stageName(p.step)}
                  </span>
                </span>
                <MiniStages idx={p.step} />
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Mono style={{ fontSize: 11, color: 'var(--hf-ink)' }}>{p.progress}%</Mono>
                <Bar p={p} row />
              </span>
              {money &&
                (p.budget != null ? (
                  <BudgetBar total={p.budget} used={p.fin?.cost ?? null} />
                ) : (
                  <Mono dim style={{ fontSize: 11 }}>
                    —
                  </Mono>
                ))}
              <Delivery p={p} today={today} dateFormat={dateFormat} />
            </Link>
            <Menu
              label={`Actions for ${p.name}`}
              items={rowMenu(p, navigate, isStarred ? () => toggleStar(p) : null, !!isStarred?.(p))}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const kicker = {
  display: 'block',
  fontSize: 'var(--hf-text-2xs)',
  color: 'var(--hf-ink-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: 3,
} as const;
const small = { display: 'block', fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' };

const ProjectCard = ({
  p,
  today,
  dateFormat,
  star,
}: {
  p: Project;
  today: string;
  dateFormat: string;
  star: { on: boolean; toggle: () => void } | null;
}) => (
  <div
    className="hf-glass"
    style={{
      borderRadius: 12,
      position: 'relative',
      overflow: 'hidden',
      border: `1px solid ${
        p.state === 'blocked'
          ? 'var(--hf-rose)'
          : p.state === 'risk'
            ? 'var(--hf-amber-line)'
            : 'var(--hf-rule-2)'
      }`,
    }}
  >
    {/* Accent spine */}
    <span
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: 3,
        background: projectColor(p.id),
        opacity: 0.9,
      }}
    />
    {star && (
      <span style={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
        <StarButton on={star.on} name={p.name} onToggle={star.toggle} size={15} />
      </span>
    )}
    <Link
      to={`/project?id=${p.id}`}
      className="hf-row-link"
      style={{ display: 'flex', flexDirection: 'column', color: 'inherit', textDecoration: 'none' }}
    >
      {/* Header */}
      <span
        style={{
          padding: '14px 40px 10px 18px',
          display: 'block',
          borderBottom: '1px solid var(--hf-rule-2)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ProjectID id={p.code ?? '—'} />
          <StatusDot state={p.state} />
        </span>
        <span
          style={{
            display: 'block',
            fontFamily: 'var(--hf-font-display)',
            fontSize: 15,
            color: 'var(--hf-ink)',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            marginBottom: 2,
          }}
        >
          {p.name}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--hf-ink-3)' }}>
          {p.client ?? 'Internal'}
        </span>
      </span>

      {/* Skills row */}
      {p.skills.length > 0 && (
        <span
          style={{
            padding: '10px 16px 10px 18px',
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--hf-rule-2)',
          }}
        >
          {p.skills.map((s) => (
            <span
              key={s}
              style={{
                fontSize: 'var(--hf-text-2xs)',
                padding: '2px 7px',
                borderRadius: 3,
                background: 'var(--hf-surface-2)',
                color: 'var(--hf-ink-2)',
                border: '1px solid var(--hf-rule-2)',
                fontFamily: 'var(--hf-font-mono)',
              }}
            >
              {s.toLowerCase()}
            </span>
          ))}
        </span>
      )}

      {/* Stage + progress */}
      <span
        style={{ padding: '12px 16px 10px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StageDot p={p} />
            <span style={{ fontSize: 11.5, color: 'var(--hf-ink-2)' }}>{stageName(p.step)}</span>
          </span>
          <Mono style={{ fontSize: 11, color: 'var(--hf-ink)' }}>{p.progress}%</Mono>
        </span>
        <Bar p={p} />
        <MiniStages idx={p.step} />
      </span>

      {/* Footer stats */}
      <span
        style={{
          padding: '12px 16px 14px 18px',
          display: 'grid',
          gridTemplateColumns: p.budget != null ? '1fr 1fr 1fr' : '1fr 1fr',
          gap: 12,
          borderTop: '1px solid var(--hf-rule-2)',
          background: 'var(--hf-surface)',
        }}
      >
        <span>
          <span style={kicker}>Delivery</span>
          <Delivery p={p} today={today} dateFormat={dateFormat} />
        </span>
        {p.budget != null && (
          <span>
            <span style={kicker}>Budget</span>
            <Mono style={{ fontSize: 11.5, color: 'var(--hf-ink)' }}>{fmtINR(p.budget)}</Mono>
            {p.fin && <span style={small}>{fmtINR(p.fin.cost)} spent</span>}
          </span>
        )}
        <span>
          <span style={kicker}>Team</span>
          {p.team.length ? (
            <AvatarGroup names={p.team} max={3} size={20} />
          ) : (
            <span style={small}>No team yet</span>
          )}
          {p.hours != null && <span style={{ ...small, marginTop: 3 }}>{p.hours}h logged</span>}
        </span>
      </span>

      {/* Activity pulse */}
      <span
        style={{
          padding: '8px 16px 10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          borderTop: '1px solid var(--hf-rule-2)',
        }}
      >
        <span
          aria-hidden="true"
          className="hf-pulse-dot"
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: p.state === 'shipped' ? 'var(--hf-ink-3)' : 'var(--hf-accent)',
          }}
        />
        {p.activity ? (
          <>
            <span>Last activity</span>
            <span style={{ color: 'var(--hf-ink-2)' }}>{fmtRelative(p.activity)}</span>
          </>
        ) : (
          <span>No work items yet</span>
        )}
        <span style={{ flex: 1 }} />
        <span aria-hidden="true">→</span>
      </span>
    </Link>
  </div>
);

const ellipsis = {
  color: 'var(--hf-ink)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
} as const;

const Team = ({ p }: { p: Project }) =>
  p.team.length ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <AvatarGroup names={p.team} max={3} size={22} />
      <span style={{ fontSize: 11, color: 'var(--hf-ink-3)', whiteSpace: 'nowrap' }}>
        {(p.leadName ?? p.team[0]).split(' ')[0]}
        {p.team.length > 1 ? ` +${p.team.length - 1}` : ''}
      </span>
      <span className="hf-sr-only">{p.team.join(', ')}</span>
    </span>
  ) : (
    <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>No team yet</span>
  );

// The manual way a project comes to exist from a deal the Zoho sync has mirrored.
const SetUpFromDeal = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  const { push } = useToast();
  const [dealId, setDealId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async () => {
    if (!supabase) return;
    const id = dealId.trim();
    if (!id) {
      setError('Enter the deal’s ID from Zoho CRM.');
      return;
    }
    setBusy(true);
    setError(undefined);
    const { data, error: e } = await supabase.rpc('create_project_from_deal', { p_deal_id: id });
    setBusy(false);
    if (e) {
      setError(dealSetupError(e.message));
      return;
    }
    push({ tone: 'success', message: 'Project set up from the deal.' });
    onClose();
    navigate(`/project?id=${data as string}`);
  };
  return (
    <Dialog
      open
      title="Set up a project from a deal"
      onClose={onClose}
      dirty={dealId.trim() !== '' && !busy}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Set up project
          </Button>
        </>
      )}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p style={{ margin: 0, fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
          A won deal sets up its project by itself. Use this when you need one sooner. The deal’s
          name, client, dates and manager come from Zoho.
        </p>
        <Field
          label="Zoho deal ID"
          hint="The long number at the end of the deal’s address in Zoho CRM."
          error={error}
          required
        >
          <TextInput
            mono
            value={dealId}
            onChange={setDealId}
            inputMode="numeric"
            autoComplete="off"
          />
        </Field>
      </form>
    </Dialog>
  );
};
